import { type FormEvent, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import FinanceDateFilter from "../../components/finance/FinanceDateFilter";
import FinanceFilterField from "../../components/finance/FinanceFilterField";
import FinanceFilterPanel from "../../components/finance/FinanceFilterPanel";
import FinanceMultiSelect from "../../components/finance/FinanceMultiSelect";
import FinancePagination from "../../components/finance/FinancePagination";
import FinanceWarningBanner from "../../components/finance/FinanceWarningBanner";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import { fetchFinanceDataQuality } from "../../finance/data-quality-api";
import type {
  FinanceDataQualityFilters,
  FinanceDataQualityScope,
  FinanceDataQualitySeverity,
  FinanceDataQualityResponse,
} from "../../finance/data-quality-types";
import {
  financeDateRange,
  type FinanceDateRangeValue,
} from "../../finance/dates";

const emptyFilters: FinanceDataQualityFilters = {
  areas: [],
  severities: [],
  reports: [],
  issueTypes: [],
  classIds: [],
  categoryIds: [],
  productIds: [],
  customerIds: [],
  supplierIds: [],
  currencyIds: [],
  scopes: [],
};

function formatDate(value: string | null) {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
}

function formatArea(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function SeverityBadge({ severity }: { severity: FinanceDataQualitySeverity }) {
  return severity === "critical" ? (
    <span className="inline-flex rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-800">
      Critical
    </span>
  ) : (
    <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
      Warning
    </span>
  );
}

function SummaryCard({
  label,
  value,
  tone = "slate",
}: {
  label: string;
  value: number | string;
  tone?: "red" | "amber" | "slate";
}) {
  const valueColour = {
    red: "text-red-700",
    amber: "text-amber-700",
    slate: "text-slate-900",
  }[tone];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-sm font-medium text-slate-500">{label}</p>
      <p className={`mt-2 text-3xl font-semibold ${valueColour}`}>{value}</p>
    </div>
  );
}

export default function FinanceDataQuality() {
  const [range, setRange] = useState<FinanceDateRangeValue>(
    financeDateRange("this_month"),
  );
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [filters, setFilters] =
    useState<FinanceDataQualityFilters>(emptyFilters);
  const [report, setReport] = useState<FinanceDataQualityResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const stableFilters = useMemo(() => filters, [filters]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchFinanceDataQuality(range, page, search, stableFilters)
      .then((response) => {
        if (active) {
          setReport(response);
        }
      })
      .catch(() => {
        if (active) {
          setError("Finance data quality could not be loaded.");
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [range.from, range.to, page, search, stableFilters]);

  const updateFilter = <K extends keyof FinanceDataQualityFilters>(
    key: K,
    value: FinanceDataQualityFilters[K],
  ) => {
    setFilters((current) => ({ ...current, [key]: value }));
    setPage(1);
  };

  const changeClass = (classIds: number[]) => {
    setFilters((current) => ({
      ...current,
      classIds,
      categoryIds: [],
      productIds: [],
    }));
    setPage(1);
  };

  const changeCategory = (categoryIds: number[]) => {
    setFilters((current) => ({
      ...current,
      categoryIds,
      productIds: [],
    }));
    setPage(1);
  };

  const changeRange = (value: FinanceDateRangeValue) => {
    setRange(value);
    setPage(1);
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  };

  const clearFilters = () => {
    setFilters(emptyFilters);
    setSearch("");
    setSearchDraft("");
    setPage(1);
  };

  const categoryOptions =
    report?.filters.categories.filter(
      (option) =>
        filters.classIds.length === 0 ||
        (option.class_id !== undefined &&
          option.class_id !== null &&
          filters.classIds.includes(option.class_id)),
    ) ?? [];
  const productOptions =
    report?.filters.products.filter(
      (option) =>
        filters.categoryIds.length === 0 ||
        (option.category_id !== undefined &&
          option.category_id !== null &&
          filters.categoryIds.includes(option.category_id)),
    ) ?? [];
  const additionalFilterCount =
    Number(filters.categoryIds.length > 0) +
    Number(filters.productIds.length > 0) +
    Number(filters.customerIds.length > 0) +
    Number(filters.supplierIds.length > 0) +
    Number(filters.currencyIds.length > 0) +
    Number(filters.scopes.length > 0);
  const hasActiveFilters =
    search !== "" || Object.values(filters).some((values) => values.length > 0);
  const reportLabels = Object.fromEntries(
    report?.report_reliability.map((item) => [item.key, item.label]) ?? [],
  );

  return (
    <main className="min-h-screen bg-slate-50 px-4 py-6 sm:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-sm font-medium text-emerald-700">Finance</p>
            <h1 className="text-2xl font-semibold text-slate-900">
              Finance Data Quality
            </h1>
            <p className="mt-1 max-w-3xl text-sm text-slate-600">
              See what information is missing, which Finance figures may be
              unreliable, and where the source records can be corrected.
            </p>
          </div>
          <FinanceDateFilter value={range} onChange={changeRange} />
        </div>

        {report && report.summary.critical_issues > 0 && (
          <div className="mt-5">
            <FinanceWarningBanner>
              Some Finance figures may be unreliable for the selected period or
              current inventory. Review the critical issues below before relying
              on affected totals or profitability.
            </FinanceWarningBanner>
          </div>
        )}

        <section className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            label="Critical Issues"
            value={report?.summary.critical_issues ?? "—"}
            tone="red"
          />
          <SummaryCard
            label="Warnings"
            value={report?.summary.warnings ?? "—"}
            tone="amber"
          />
          <SummaryCard
            label="Affected Records"
            value={report?.summary.affected_records ?? "—"}
          />
          <SummaryCard
            label="Reports Affected"
            value={report?.summary.reports_affected ?? "—"}
          />
        </section>

        <section className="mt-6 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div>
            <h2 className="font-semibold text-slate-900">
              Finance report reliability
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              A report can remain reliable overall even when a warning affects
              only one grouping or secondary metric.
            </p>
          </div>
          <div className="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {report?.report_reliability.map((item) => (
              <div
                key={item.key}
                className="rounded-xl border border-slate-200 p-4"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="font-semibold text-slate-900">{item.label}</p>
                  {item.status === "reliable" ? (
                    <span className="rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-800">
                      Reliable
                    </span>
                  ) : (
                    <span className="rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">
                      Needs attention
                    </span>
                  )}
                </div>
                {item.status === "reliable" ? (
                  <p className="mt-2 text-sm text-slate-500">
                    No known reliability issues were found.
                  </p>
                ) : (
                  <>
                    <p className="mt-2 text-sm text-slate-600">
                      {item.critical_issues} critical · {item.warnings} warning
                      {item.warnings === 1 ? "" : "s"}
                    </p>
                    <p className="mt-2 text-xs leading-5 text-slate-500">
                      May affect: {item.unreliable_metrics.join(", ")}
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
          <FinanceFilterPanel
            additionalFilterCount={additionalFilterCount}
            hasActiveFilters={hasActiveFilters}
            onClear={clearFilters}
            primary={
              <>
                <FinanceFilterField label="Finance area">
                  <FinanceMultiSelect
                    value={filters.areas}
                    placeholder="All areas"
                    options={
                      report?.filters.areas.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("areas", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Severity">
                  <FinanceMultiSelect<FinanceDataQualitySeverity>
                    value={filters.severities}
                    placeholder="All severities"
                    options={
                      report?.filters.severities.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("severities", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Report affected">
                  <FinanceMultiSelect
                    value={filters.reports}
                    placeholder="All reports"
                    options={
                      report?.filters.reports.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("reports", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Issue type">
                  <FinanceMultiSelect
                    value={filters.issueTypes}
                    placeholder="All issue types"
                    options={
                      report?.filters.issue_types.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("issueTypes", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Class">
                  <FinanceMultiSelect
                    value={filters.classIds}
                    placeholder="All classes"
                    options={
                      report?.filters.classes.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={changeClass}
                  />
                </FinanceFilterField>
              </>
            }
            additional={
              <>
                <FinanceFilterField label="Category">
                  <FinanceMultiSelect
                    value={filters.categoryIds}
                    placeholder="All categories"
                    options={categoryOptions.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    onChange={changeCategory}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Product">
                  <FinanceMultiSelect
                    value={filters.productIds}
                    placeholder="All products"
                    options={productOptions.map((option) => ({
                      label: option.name,
                      value: option.id,
                    }))}
                    onChange={(value) => updateFilter("productIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Customer">
                  <FinanceMultiSelect
                    value={filters.customerIds}
                    placeholder="All customers"
                    options={
                      report?.filters.customers.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("customerIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Supplier">
                  <FinanceMultiSelect
                    value={filters.supplierIds}
                    placeholder="All suppliers"
                    options={
                      report?.filters.suppliers.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("supplierIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Currency">
                  <FinanceMultiSelect
                    value={filters.currencyIds}
                    placeholder="All currencies"
                    options={
                      report?.filters.currencies.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("currencyIds", value)}
                  />
                </FinanceFilterField>
                <FinanceFilterField label="Data scope">
                  <FinanceMultiSelect<FinanceDataQualityScope>
                    value={filters.scopes}
                    placeholder="All scopes"
                    options={
                      report?.filters.scopes.map((option) => ({
                        label: option.name,
                        value: option.id,
                      })) ?? []
                    }
                    onChange={(value) => updateFilter("scopes", value)}
                  />
                </FinanceFilterField>
              </>
            }
          />

          <div className="flex flex-col gap-3 border-b border-slate-200 p-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="font-semibold text-slate-900">
                Reliability issues
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                Inventory findings describe current inventory; other findings
                follow the selected reporting period.
              </p>
            </div>
            <form className="flex gap-2" onSubmit={submitSearch}>
              <label className="sr-only" htmlFor="data-quality-search">
                Search data quality issues
              </label>
              <input
                id="data-quality-search"
                className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                placeholder="Record, issue, or metric"
                value={searchDraft}
                onChange={(event) => setSearchDraft(event.target.value)}
              />
              <button
                className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                type="submit"
              >
                Search
              </button>
            </form>
          </div>

          {error && (
            <p className="p-5 text-sm font-medium text-red-700">{error}</p>
          )}
          {!error && loading && (
            <div className="p-3">
              <TableLoadingSkeleton rows={7} columns={6} />
            </div>
          )}
          {!error && !loading && report?.issues.length === 0 && (
            <p className="p-5 text-sm text-slate-500">
              No data quality issues match the selected period and filters.
            </p>
          )}
          {!error && !loading && report && report.issues.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                    <tr>
                      <th className="px-4 py-3">Severity</th>
                      <th className="px-4 py-3">Area / Issue</th>
                      <th className="px-4 py-3">Record</th>
                      <th className="px-4 py-3">What may be unreliable</th>
                      <th className="px-4 py-3">Scope / Date</th>
                      <th className="px-4 py-3">Impact</th>
                      <th className="px-4 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {report.issues.map((issue) => (
                      <tr key={issue.id} className="align-top">
                        <td className="px-4 py-4">
                          <SeverityBadge severity={issue.severity} />
                        </td>
                        <td className="min-w-64 px-4 py-4">
                          <p className="font-semibold text-slate-900">
                            {formatArea(issue.finance_area)} ·{" "}
                            {issue.issue_label}
                          </p>
                          <p className="mt-1 text-xs leading-5 text-slate-500">
                            {issue.message}
                          </p>
                        </td>
                        <td className="px-4 py-4 font-medium text-slate-800">
                          {issue.record_label}
                        </td>
                        <td className="min-w-52 px-4 py-4 text-slate-600">
                          <p>{issue.unreliable_metrics.join(", ")}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Reports:{" "}
                            {issue.affected_reports
                              .map(
                                (reportKey) =>
                                  reportLabels[reportKey] ?? reportKey,
                              )
                              .join(", ")}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          <span className="whitespace-nowrap">
                            {issue.scope === "current_inventory"
                              ? "Current inventory"
                              : "Selected period"}
                          </span>
                          <p className="mt-1 whitespace-nowrap text-xs text-slate-500">
                            {formatDate(issue.occurred_at)}
                          </p>
                        </td>
                        <td className="px-4 py-4 text-slate-600">
                          {issue.financial_impact === null
                            ? "Unknown"
                            : issue.financial_impact.toLocaleString("en-CA", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                        </td>
                        <td className="px-4 py-4">
                          <Link
                            className="whitespace-nowrap font-semibold text-emerald-700 hover:text-emerald-800"
                            to={issue.correction_url}
                          >
                            Review record
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <FinancePagination
                label="issues"
                page={report.pagination.current_page}
                lastPage={report.pagination.last_page}
                total={report.pagination.total}
                onPageChange={setPage}
              />
            </>
          )}
        </section>
      </div>
    </main>
  );
}
